/**
 * Deferred Apple plan changes (annual → monthly) stay on the old product until
 * renewal. Persist the scheduled switch so Membership can show it.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import type { CustomerInfo } from 'react-native-purchases';
import { logger } from './logger';
import {
  getActiveMembershipEntitlement,
  grantAllPlanFamily,
} from './purchases/RevenueCat';
import { formatDateTime, productDisplayName } from './purchases/subscriptionStatus';

export type PendingPlanChange = {
  currentProductId: string;
  newProductId: string;
  currentPlan: string;
  newPlan: string;
  switchDate: string;
  switchAtMs: number | null;
};

function storageKey(userId: string): string {
  return `pending_plan_change:${userId}`;
}

function storageUserId(
  supabaseUserId: string | null | undefined,
  info: CustomerInfo | null,
): string | null {
  return supabaseUserId ?? info?.originalAppUserId ?? null;
}

async function readRaw(key: string): Promise<string | null> {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
    return localStorage.getItem(key);
  }
  return AsyncStorage.getItem(key);
}

async function writeRaw(key: string, value: string | null): Promise<void> {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
    return;
  }
  if (value === null) await AsyncStorage.removeItem(key);
  else await AsyncStorage.setItem(key, value);
}

export async function savePendingPlanChange(
  userId: string,
  change: PendingPlanChange,
): Promise<void> {
  if (!userId) return;
  try {
    await writeRaw(storageKey(userId), JSON.stringify(change));
  } catch (err) {
    logger.error('[pendingPlanChange] save failed:', err);
  }
}

export async function clearPendingPlanChange(userId: string): Promise<void> {
  if (!userId) return;
  try {
    await writeRaw(storageKey(userId), null);
  } catch (err) {
    logger.error('[pendingPlanChange] clear failed:', err);
  }
}

export async function readPendingPlanChange(
  userId: string,
): Promise<PendingPlanChange | null> {
  if (!userId) return null;
  try {
    const raw = await readRaw(storageKey(userId));
    if (!raw) return null;
    return JSON.parse(raw) as PendingPlanChange;
  } catch (err) {
    logger.error('[pendingPlanChange] read failed:', err);
    return null;
  }
}

export async function reconcilePendingPlanChange(
  userId: string,
  activeProductId: string | null,
): Promise<PendingPlanChange | null> {
  const pending = await readPendingPlanChange(userId);
  if (!pending) return null;
  if (!activeProductId) return pending;

  if (activeProductId === pending.newProductId) {
    await clearPendingPlanChange(userId);
    return null;
  }
  if (activeProductId !== pending.currentProductId) {
    await clearPendingPlanChange(userId);
    return null;
  }
  return pending;
}

/**
 * Infer a deferred plan change from RevenueCat: entitlement still on the old
 * product, but a newer same-tier product is set to renew.
 */
export function detectPendingPlanChange(
  info: CustomerInfo | null,
): PendingPlanChange | null {
  const entitlement = getActiveMembershipEntitlement(info);
  if (!info || !entitlement?.willRenew || !entitlement.expirationDate) return null;

  const currentProductId = entitlement.productIdentifier;
  const family = grantAllPlanFamily(currentProductId);
  if (!family) return null;

  const subs = info.subscriptionsByProductIdentifier ?? {};
  const activeLatestMs = entitlement.latestPurchaseDateMillis;

  let best: { id: string; purchaseMs: number } | null = null;

  for (const [productId, sub] of Object.entries(subs)) {
    if (productId === currentProductId) continue;
    if (grantAllPlanFamily(productId) !== family) continue;
    if (!sub.willRenew) continue;

    const purchaseMs = Date.parse(sub.purchaseDate);
    if (Number.isNaN(purchaseMs) || purchaseMs < activeLatestMs) continue;

    if (!best || purchaseMs > best.purchaseMs) {
      best = { id: productId, purchaseMs };
    }
  }

  if (!best) {
    for (const productId of info.allPurchasedProductIdentifiers) {
      if (productId === currentProductId) continue;
      if (grantAllPlanFamily(productId) !== family) continue;
      const purchaseMs = Date.parse(info.allPurchaseDates[productId] ?? '');
      if (Number.isNaN(purchaseMs) || purchaseMs < activeLatestMs) continue;
      if (!best || purchaseMs > best.purchaseMs) {
        best = { id: productId, purchaseMs };
      }
    }
  }

  if (!best) return null;

  const periodEnd = new Date(entitlement.expirationDate);
  if (Number.isNaN(periodEnd.getTime())) return null;

  return {
    currentProductId,
    newProductId: best.id,
    currentPlan: productDisplayName(currentProductId) ?? currentProductId,
    newPlan: productDisplayName(best.id) ?? best.id,
    switchDate: formatDateTime(periodEnd),
    switchAtMs: periodEnd.getTime(),
  };
}

export async function resolvePendingPlanChange(
  supabaseUserId: string | null | undefined,
  info: CustomerInfo | null,
): Promise<PendingPlanChange | null> {
  const entitlement = getActiveMembershipEntitlement(info);
  const activeProductId = entitlement?.productIdentifier ?? null;
  const userId = storageUserId(supabaseUserId, info);

  if (entitlement && !entitlement.willRenew) {
    if (userId) await clearPendingPlanChange(userId);
    return null;
  }

  if (userId) {
    const stored = await reconcilePendingPlanChange(userId, activeProductId);
    if (stored) return stored;
  }

  const detected = detectPendingPlanChange(info);
  if (detected && userId) {
    await savePendingPlanChange(userId, detected);
  }
  return detected;
}

export function buildPendingPlanChange(
  currentProductId: string,
  newProductId: string,
  periodEnd: Date,
): PendingPlanChange {
  return {
    currentProductId,
    newProductId,
    currentPlan: productDisplayName(currentProductId) ?? currentProductId,
    newPlan: productDisplayName(newProductId) ?? newProductId,
    switchDate: formatDateTime(periodEnd),
    switchAtMs: periodEnd.getTime(),
  };
}
